import { NotificationItem } from "@/src/models/notification.model";
import { useGetNotifications } from "@/src/services/api/notification/notification.hooks";
import { useTheme } from "@/src/styles/theme-provider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ScrollView, Text, useWindowDimensions, View } from "react-native";
import { NotificationRow } from "../../notification/notification-row";
import NotificationSkeleton from "../../notification/notification-skeleton";

const getDateLabel = (dateStr: string): string => {
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (isSameDay(date, today)) return "Today";
  if (isSameDay(date, yesterday)) return "Yesterday";
  return date.toLocaleDateString("en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

type NotificationGroup = { label: string; items: NotificationItem[] };

const groupByDate = (
  notifications: NotificationItem[],
): NotificationGroup[] => {
  const groups: NotificationGroup[] = [];
  let currentLabel = "";
  let currentItems: NotificationItem[] = [];
  for (const item of notifications) {
    const label = getDateLabel(item.createdTime);
    if (label !== currentLabel) {
      if (currentItems.length > 0)
        groups.push({ label: currentLabel, items: currentItems });
      currentLabel = label;
      currentItems = [item];
    } else {
      currentItems.push(item);
    }
  }
  if (currentItems.length > 0)
    groups.push({ label: currentLabel, items: currentItems });
  return groups;
};

const TIMELINE_DOT_COLORS = [
  "--color-crimson-default",
  "--color-primary-default",
  "--color-jade-default",
  "--color-warning-default",
  "--color-info-default",
] as const;

const Notifications = () => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  const { width: windowWidth } = useWindowDimensions();
  const isMobile = windowWidth < 768;

  const [page, setPage] = useState(0);
  const { data, isPending } = useGetNotifications({
    Page: page,
    PageSize: 10,
    IsUnreadOnly: true,
    SortIsDescending: false,
    SortProperty: "createdTime",
  });
  const [allNotifications, setAllNotifications] = useState<NotificationItem[]>(
    [],
  );

  useEffect(() => {
    if (data?.notifications) {
      setAllNotifications((prev) =>
        page === 0 ? data.notifications : [...prev, ...data.notifications],
      );
    }
  }, [data]);

  const groups = groupByDate(allNotifications);

  if (isPending && allNotifications.length === 0) {
    return <NotificationSkeleton count={6} />;
  }

  if (groups.length === 0) {
    return (
      <View className="flex-1 items-center justify-center py-20">
        <Text className="text-sm text-ash-1">{t("LABEL_NO_NOTIFCATIONS")}</Text>
      </View>
    );
  }

  return (
    <ScrollView
      showsVerticalScrollIndicator={false}
      contentContainerStyle={{
        paddingVertical: 16,
        paddingRight: isMobile ? 0 : 16,
      }}
      onScroll={({ nativeEvent }) => {
        const { layoutMeasurement, contentOffset, contentSize } = nativeEvent;
        if (
          layoutMeasurement.height + contentOffset.y >=
          contentSize.height - 40
        ) {
          if (allNotifications.length >= 10) setPage((prev) => prev + 1);
        }
      }}
      scrollEventThrottle={400}
    >
      {groups.map((group, groupIndex) => {
        const dotColorKey =
          TIMELINE_DOT_COLORS[groupIndex % TIMELINE_DOT_COLORS.length];
        const dotColor = themeVars[dotColorKey] as string;

        if (isMobile) {
          return (
            <View key={group.label} className="mb-3">
              {/* Section label */}
              <Text className="text-xs font-semibold uppercase tracking-wide text-ash-1 mb-1 px-3">
                {group.label}
              </Text>

              {/* Rows */}
              <View className="gap-1">
                {group.items.map((item) => (
                  <NotificationRow key={item.id} item={item} />
                ))}
              </View>
            </View>
          );
        }

        return (
          <View key={group.label} className="flex-row">
            {/* ── LEFT timeline column (web only) ── */}
            <View className="w-20 items-center">
              <View className="h-9 justify-center items-center">
                <Text
                  className="text-xs text-ash-1 font-medium text-center w-16"
                  numberOfLines={2}
                >
                  {group.label}
                </Text>
              </View>
              <View
                className="w-3 h-3 rounded-full -mt-0.5"
                style={{
                  borderWidth: 2,
                  borderColor: dotColor,
                  backgroundColor: themeVars["--color-chalk-default"] as string,
                }}
              />
              <View
                className="flex-1 w-0.5 mt-1 min-h-5"
                style={{
                  backgroundColor: themeVars["--color-ash-3"] as string,
                }}
              />
            </View>

            {/* ── RIGHT cards column ── */}
            <View className="flex-1">
              <View className="h-9" />
              {group.items.map((item) => (
                <NotificationRow key={item.id} item={item} />
              ))}
              <View className="h-4" />
            </View>
          </View>
        );
      })}
    </ScrollView>
  );
};

export default Notifications;
